import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { VisaDialog } from '@/components/visas/VisaDialog';
import { ViewToggle } from '@/components/shared/ViewToggle';
import { VisaCard } from '@/components/visas/VisaCard';
interface Visa {
  id: string;
  visa_type_id: string;
  client_name: string;
  passport_number: string;
  status: string;
  issue_date: string;
  expiry_date: string;
  notes: string;
  created_at: string;
  updated_at: string;
  visa_types?: {
    name_ar: string;
    name_en: string;
  };
}
export default function Visas() {
  const [visas, setVisas] = useState<Visa[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVisa, setEditingVisa] = useState<Visa | null>(null);
  const [view, setView] = useState<'table' | 'cards'>('table');
  const {
    toast
  } = useToast();
  const fetchVisas = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from('visas').select(`
          *,
          visa_types (
            name_ar,
            name_en
          )
        `).order('created_at', {
        ascending: false
      });
      if (error) throw error;
      setVisas(data || []);
    } catch (error) {
      console.error('Error fetching visas:', error);
      toast({
        title: "خطأ",
        description: "فشل في تحميل التأشيرات",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذه التأشيرة؟')) return;
    try {
      const {
        error
      } = await supabase.from('visas').delete().eq('id', id);
      if (error) throw error;
      toast({
        title: "تم الحذف",
        description: "تم حذف التأشيرة بنجاح"
      });
      fetchVisas();
    } catch (error) {
      console.error('Error deleting visa:', error);
      toast({
        title: "خطأ",
        description: "فشل في حذف التأشيرة",
        variant: "destructive"
      });
    }
  };
  const handleEdit = (visa: Visa) => {
    setEditingVisa(visa);
    setDialogOpen(true);
  };
  const handleAdd = () => {
    setEditingVisa(null);
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingVisa(null);
    fetchVisas();
  };
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'approved':
        return 'default';
      case 'pending':
        return 'secondary';
      case 'rejected':
        return 'destructive';
      default:
        return 'outline';
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved':
        return 'مقبولة';
      case 'pending':
        return 'قيد المراجعة';
      case 'rejected':
        return 'مرفوضة';
      default:
        return status;
    }
  };
  useEffect(() => {
    fetchVisas();
  }, []);
  if (loading) {
    return <div className="flex items-center justify-center h-96">
        <div className="text-lg">جاري التحميل...</div>
      </div>;
  }
  return <div className="space-y-6">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">التأشيرات</h1>
        
        <ViewToggle view={view} onViewChange={v => setView(v as 'table' | 'cards')} />
        
        <Button onClick={handleAdd} className="gap-2">
          <Plus className="h-4 w-4" />
          إضافة تأشيرة
        </Button>
      </div>

      {view === 'table' ? <Card>
          <CardHeader>
            <CardTitle>قائمة التأشيرات</CardTitle>
            
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">اسم العميل</TableHead>
                  <TableHead className="text-right">نوع التأشيرة</TableHead>
                  <TableHead className="text-right">رقم الجواز</TableHead>
                  <TableHead className="text-right">الحالة</TableHead>
                  <TableHead className="text-right">تاريخ الإصدار</TableHead>
                  <TableHead className="text-right">تاريخ الانتهاء</TableHead>
                  <TableHead className="text-right">العمليات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visas.map(visa => <TableRow key={visa.id}>
                    <TableCell className="font-medium">{visa.client_name}</TableCell>
                    <TableCell>{visa.visa_types?.name_ar}</TableCell>
                    <TableCell>{visa.passport_number}</TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(visa.status)}>
                        {getStatusText(visa.status)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {visa.issue_date ? new Date(visa.issue_date).toLocaleDateString('ar-SA') : '-'}
                    </TableCell>
                    <TableCell>
                      {visa.expiry_date ? new Date(visa.expiry_date).toLocaleDateString('ar-SA') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(visa)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(visa.id)} className="text-red-600 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </CardContent>
        </Card> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visas.map(visa => <VisaCard key={visa.id} visa={visa} onEdit={() => handleEdit(visa)} onDelete={() => handleDelete(visa.id)} />)}
        </div>}

      <VisaDialog open={dialogOpen} onOpenChange={setDialogOpen} visa={editingVisa} onClose={handleDialogClose} />
    </div>;
}